import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCcw, Loader2, Save, AlertTriangle, DollarSign, GraduationCap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const BILLING_MODELS = ["per_course", "per_credit"];
const CREDENTIAL_TIERS = ["certificate", "practitioner", "doctoral"];

function authHeaders() {
  const t = localStorage.getItem("kcis_admin_token");
  return { Authorization: `Bearer ${t}` };
}

function CeilingBadge({ perCourse, perCreditHours, perCreditRate }) {
  let implied = 0;
  if (perCreditRate) implied = Number(perCreditRate);
  else if (perCourse && perCreditHours) implied = Number(perCourse) / Number(perCreditHours);
  if (!implied || Number.isNaN(implied)) return null;
  const over = implied > 250;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-mono ${
        over ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"
      }`}
      data-testid="ceiling-badge"
    >
      {over && <AlertTriangle className="w-3 h-3" />}
      {`$${implied.toFixed(2)}/cr`} {over ? "· over ceiling" : "· OK"}
    </span>
  );
}

function ProgramEditor({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState({ ...initial });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      ["num_courses", "pay_in_full_price", "per_course_price", "per_credit_rate",
       "credit_hours_per_course", "total_clock_hours", "order"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
          delete payload[k];
        } else if (k === "num_courses" || k === "order") {
          payload[k] = parseInt(payload[k], 10);
        } else {
          payload[k] = Number(payload[k]);
        }
      });
      const r = await fetch(`${API}/api/admin/kuhas/programs/${form.slug}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      });
      const body = await r.json();
      if (!r.ok) {
        const detail = body?.detail || body;
        if (detail?.code === "per_credit_ceiling_exceeded") {
          toast.error(
            `Rejected — $${detail.implied_per_credit_rate}/cr exceeds ceiling $${detail.max_per_credit_rate}/cr`
          );
        } else {
          toast.error(detail?.message || detail?.code || `HTTP ${r.status}`);
        }
        return;
      }
      toast.success("Program saved");
      onSaved(body.program);
    } catch (e) {
      toast.error(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4"
      data-testid={`program-editor-${form.slug}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-xl text-slate-900">{form.title || form.slug}</div>
          <div className="text-xs text-slate-500 font-mono">{form.slug}</div>
        </div>
        <CeilingBadge
          perCourse={form.per_course_price}
          perCreditHours={form.credit_hours_per_course}
          perCreditRate={form.billing_model === "per_credit" ? form.per_credit_rate : null}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block text-xs">
          <span className="text-slate-600">Title</span>
          <input className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm"
                 value={form.title || ""} onChange={(e) => set("title", e.target.value)} required
                 data-testid={`field-title-${form.slug}`} />
        </label>
        <label className="block text-xs">
          <span className="text-slate-600">Level label</span>
          <input className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm"
                 value={form.level_label || ""} onChange={(e) => set("level_label", e.target.value)} />
        </label>
        <label className="block text-xs">
          <span className="text-slate-600">Billing model</span>
          <select className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm bg-white"
                  value={form.billing_model} onChange={(e) => set("billing_model", e.target.value)}
                  data-testid={`field-billing-${form.slug}`}>
            {BILLING_MODELS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        <label className="block text-xs">
          <span className="text-slate-600">Credential tier</span>
          <select className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm bg-white"
                  value={form.credential_tier || "certificate"} onChange={(e) => set("credential_tier", e.target.value)}>
            {CREDENTIAL_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="block text-xs">
          <span className="text-slate-600">Num courses</span>
          <input type="number" min="0" className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm"
                 value={form.num_courses ?? 0} onChange={(e) => set("num_courses", e.target.value)} />
        </label>
        <label className="block text-xs">
          <span className="text-slate-600">Pay-in-full price ($)</span>
          <input type="number" step="0.01" className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm"
                 value={form.pay_in_full_price ?? ""} onChange={(e) => set("pay_in_full_price", e.target.value)} />
        </label>
        {form.billing_model === "per_course" && (
          <label className="block text-xs">
            <span className="text-slate-600">Per-course price ($)</span>
            <input type="number" step="0.01" className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm"
                   value={form.per_course_price ?? ""} onChange={(e) => set("per_course_price", e.target.value)}
                   data-testid={`field-per-course-${form.slug}`} />
          </label>
        )}
        {form.billing_model === "per_credit" && (
          <label className="block text-xs">
            <span className="text-slate-600">Per-credit rate ($) — max $250</span>
            <input type="number" step="0.01" className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm"
                   value={form.per_credit_rate ?? ""} onChange={(e) => set("per_credit_rate", e.target.value)} />
          </label>
        )}
        <label className="block text-xs">
          <span className="text-slate-600">Credit hours per course</span>
          <input type="number" step="0.1" className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm"
                 value={form.credit_hours_per_course ?? ""} onChange={(e) => set("credit_hours_per_course", e.target.value)}
                 data-testid={`field-credit-hours-${form.slug}`} />
        </label>
        <label className="block text-xs">
          <span className="text-slate-600">Total clock hours</span>
          <input type="number" step="1" className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm"
                 value={form.total_clock_hours ?? ""} onChange={(e) => set("total_clock_hours", e.target.value)} />
        </label>
        <label className="block text-xs">
          <span className="text-slate-600">Prerequisite program slug</span>
          <input className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm font-mono"
                 value={form.prerequisite_program_slug || ""}
                 onChange={(e) => set("prerequisite_program_slug", e.target.value || null)}
                 placeholder="e.g. herbal_foundations"
                 data-testid={`field-prereq-${form.slug}`} />
        </label>
        <label className="block text-xs">
          <span className="text-slate-600">Order</span>
          <input type="number" className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm"
                 value={form.order ?? 99} onChange={(e) => set("order", e.target.value)} />
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-slate-100">
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input type="checkbox" checked={!!form.unlocks_protocol_authorship}
                 onChange={(e) => set("unlocks_protocol_authorship", e.target.checked)}
                 data-testid={`field-auth-${form.slug}`} />
          Unlocks protocol authorship
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input type="checkbox" checked={!!form.unlocks_referral_initiation}
                 onChange={(e) => set("unlocks_referral_initiation", e.target.checked)} />
          Unlocks referral initiation
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input type="checkbox" checked={!!form.requires_application}
                 onChange={(e) => set("requires_application", e.target.checked)} />
          Requires application
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input type="checkbox" checked={!!form.active}
                 onChange={(e) => set("active", e.target.checked)} />
          Active
        </label>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button type="submit" disabled={saving}
                className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium h-9 px-4 inline-flex items-center gap-1.5"
                data-testid={`save-btn-${form.slug}`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
        <button type="button" onClick={onCancel}
                className="rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm h-9 px-4"
                data-testid={`cancel-btn-${form.slug}`}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function AdminKuhasPrograms() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/kuhas/programs`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      setPrograms(body.items || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-xs text-purple-900 hover:underline flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Admin hub
            </Link>
            <span className="font-display text-lg text-slate-900">KUHAS Programs · Pricing Editor</span>
          </div>
          <button onClick={load} disabled={loading}
                  className="text-xs text-slate-700 hover:text-slate-900 flex items-center gap-1"
                  data-testid="programs-refresh-btn">
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6" data-testid="admin-kuhas-programs">
        <div>
          <h1 className="font-display text-3xl text-slate-900">Program Catalog</h1>
          <p className="mt-1 text-sm text-slate-600">
            All writes enforce the $250/credit pricing ceiling automatically. Attempts over
            the ceiling are rejected server-side; the badge on each card previews the
            implied per-credit rate before you save.
          </p>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-3">
            {programs.map((p) => (
              editing === p.slug ? (
                <ProgramEditor key={p.slug} initial={p}
                                onSaved={(updated) => { setEditing(null); load(); }}
                                onCancel={() => setEditing(null)} />
              ) : (
                <div key={p.slug}
                     className="rounded-2xl border border-slate-200 bg-white p-5 flex items-start justify-between"
                     data-testid={`program-row-${p.slug}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-purple-900" />
                      <span className="font-display text-lg text-slate-900">{p.title}</span>
                      {!p.active && <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 px-2 py-0.5 rounded">inactive</span>}
                    </div>
                    <div className="mt-1 text-xs text-slate-500 font-mono">{p.slug}</div>
                    <div className="mt-2 text-xs text-slate-700 flex flex-wrap gap-x-4 gap-y-1">
                      <span>billing: <b>{p.billing_model}</b></span>
                      <span>courses: <b>{p.num_courses}</b></span>
                      {p.pay_in_full_price && <span>pay-in-full: <b>${p.pay_in_full_price}</b></span>}
                      {p.per_course_price && <span>per-course: <b>${p.per_course_price}</b></span>}
                      {p.per_credit_rate && <span>per-credit: <b>${p.per_credit_rate}/cr</b></span>}
                      {p.slug === "dts" && p.computed_total_credit_hours !== undefined && (
                        <span>Σ credit hours: <b>{p.computed_total_credit_hours}</b></span>
                      )}
                      {p.unlocks_protocol_authorship && <span className="text-purple-900">authorship</span>}
                      {p.unlocks_referral_initiation && <span className="text-emerald-900">referral</span>}
                      {p.requires_application && <span className="text-amber-900">app-required</span>}
                      {p.prerequisite_program_slug && (
                        <span className="text-slate-500">prereq: <b>{p.prerequisite_program_slug}</b></span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <CeilingBadge
                      perCourse={p.per_course_price}
                      perCreditHours={p.credit_hours_per_course}
                      perCreditRate={p.billing_model === "per_credit" ? p.per_credit_rate : null}
                    />
                    <button
                      onClick={() => setEditing(p.slug)}
                      className="rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-xs h-8 px-3 inline-flex items-center gap-1"
                      data-testid={`edit-btn-${p.slug}`}
                    >
                      <DollarSign className="w-3.5 h-3.5" /> Edit
                    </button>
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
